import * as React from "react";
import { useState } from "react";
import { Button, ButtonToolbar, Col, Row } from "components/bootstrap";
import { DocumentTitle, IfPermitted, PageHeader, Spinner } from "components/common";
import IndicesPageNavigation from "components/indices/IndicesPageNavigation";
import withIsPermitted from "forwarder/util/withIsPermitted";
import { DATA_TIERING_LICENSE_TEXT, DATA_TIERING_LICENSE_TITLE } from "data-tiering/constants";
import useDataTiering from "data-tiering/hooks/useDataTiering";
import CreateRepositoryModal from "data-tiering/repository-management/CreateRepositoryModal";
import repositoryOptions from "data-tiering/repository-management/logic/repositoryOptions";
import { LICENSE_SUBJECTS } from "license/constants";
import LicenseCheck from "license/LicenseCheck";
import EmptyStateChecklist from "./EmptyStateChecklist";
import ReadinessWarningBanner from "./ReadinessWarningBanner";
import RepositoriesTable from "./RepositoriesTable";
import WarmTierCounters from "./WarmTierCounters";
import { WARM_TIER_DOCS } from "./constants";
import useOutstandingChecksPolling from "./hooks/useOutstandingChecksPolling";
import useRepositoriesSummary from "./hooks/useRepositoriesSummary";
const WarmTierRepositoriesPage = () => {
  var _a, _b, _c, _d, _e, _f;
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { readiness } = useDataTiering();
  const { refetchInterval, onDataLoaded } = useOutstandingChecksPolling();
  const { summary, configuredCount, credentialProblemsCount } = useRepositoriesSummary({ refetchInterval });
  const isLoading = readiness.isLoading || summary.isLoading;
  const supportedBySearchVersion = (_b = (_a = readiness.data) == null ? void 0 : _a.details.supported_by_search_version) != null ? _b : true;
  const searchRoleNodeCount = (_d = (_c = readiness.data) == null ? void 0 : _c.details.search_role_node_count) != null ? _d : 0;
  const availableBackendsCount = repositoryOptions((_f = (_e = readiness.data) == null ? void 0 : _e.details.supported_repository_types) != null ? _f : []).length;
  const readinessDegraded = searchRoleNodeCount === 0 || availableBackendsCount === 0;
  const repositoriesKnownEmpty = !summary.isError && configuredCount === 0;
  const showEmptyState = readinessDegraded && repositoriesKnownEmpty;
  const pageContent = () => {
    if (isLoading) {
      return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading Warm Tier repositories..." });
    }
    if (!supportedBySearchVersion) {
      return /* @__PURE__ */ React.createElement(EmptyStateChecklist, { supportedBySearchVersion: false });
    }
    if (showEmptyState) {
      return /* @__PURE__ */ React.createElement(EmptyStateChecklist, { supportedBySearchVersion: true });
    }
    return /* @__PURE__ */ React.createElement(React.Fragment, null, readinessDegraded && /* @__PURE__ */ React.createElement(ReadinessWarningBanner, null), /* @__PURE__ */ React.createElement(RepositoriesTable, { refetchInterval, onDataLoaded }));
  };
  return /* @__PURE__ */ React.createElement(DocumentTitle, { title: "Warm Tier Repositories" }, /* @__PURE__ */ React.createElement(IndicesPageNavigation, null), /* @__PURE__ */ React.createElement(
    PageHeader,
    {
      title: "Warm Tier Repositories",
      documentationLink: {
        title: "Data tiering documentation",
        path: WARM_TIER_DOCS.pageHeader
      },
      topActions: /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["indexsets:create"] }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: () => setShowCreateModal(true) }, "Create repository")))
    },
    /* @__PURE__ */ React.createElement("span", null, "Manage the snapshot repositories used by the warm tier and by Snapshot Archiving: create repositories, maintain their credentials, and monitor their status.")
  ), /* @__PURE__ */ React.createElement(
    LicenseCheck,
    {
      text: DATA_TIERING_LICENSE_TEXT,
      title: DATA_TIERING_LICENSE_TITLE,
      licenseSubject: LICENSE_SUBJECTS.enterprise,
      displayWarningContainer: true,
      bsStyle: "danger"
    }
  ), /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, !isLoading && supportedBySearchVersion && /* @__PURE__ */ React.createElement(
    WarmTierCounters,
    {
      searchRoleNodeCount,
      availableBackendsCount,
      configuredCount,
      credentialProblemsCount
    }
  ), pageContent())), showCreateModal && /* @__PURE__ */ React.createElement(CreateRepositoryModal, { show: true, hideModal: () => setShowCreateModal(false) }));
};
export default withIsPermitted(WarmTierRepositoriesPage, ["indexsets:read"]);
